/**
 * Reusable Confirmation Dialog Component
 * Used for confirming destructive actions (archive, delete, restore, etc.)
 */

import type { ReactNode } from 'react';

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string | ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmStyle?: 'danger' | 'warning' | 'success' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  showWarningBanner?: boolean;
  warningMessage?: string;
}

export function ConfirmationDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmStyle = 'primary',
  onConfirm,
  onCancel,
  loading = false,
  showWarningBanner = false,
  warningMessage,
}: ConfirmationDialogProps) {
  if (!isOpen) return null;

  const getConfirmButtonColor = () => {
    switch (confirmStyle) {
      case 'danger':
        return '#dc2626';
      case 'warning':
        return '#f59e0b';
      case 'success':
        return '#059669';
      case 'primary':
      default:
        return '#3b82f6';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={() => {
        if (!loading) {
          onCancel();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '16px', color: '#111827' }}>
          {title}
        </h2>

        {showWarningBanner && (
          <div
            style={{
              backgroundColor: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
            }}
          >
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#92400e', marginBottom: '8px' }}>
              ⚠️ Warning: This action cannot be undone
            </p>
            {warningMessage && (
              <p style={{ fontSize: '13px', color: '#78350f' }}>
                {warningMessage}
              </p>
            )}
          </div>
        )}

        <div style={{ marginBottom: '24px', fontSize: '16px', color: '#374151' }}>
          {message}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              backgroundColor: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{
              flex: 1,
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#ffffff',
              backgroundColor: getConfirmButtonColor(),
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? `${confirmLabel}...` : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
